/**
 * @voltxampere/ui — Quadtree<T> (Sprint 12)
 *
 * Custom MIT-clean spatial index. Per ADR-0015 we keep d3-quadtree
 * (and any other npm option) out of the dep graph — Sprint 12's
 * needs are narrow and the ~150-line surface fits inside the
 * package's import-graph discipline (Kural 4).
 *
 * Items are AABB-bounded; `queryPoint(p, tolerance)` widens the
 * cursor by `tolerance` pixels so wires are still selectable when
 * the user clicks just off-axis. `remove(id)` is O(1) via the
 * `idIndex` Map; full clear and re-insert is the planner's job
 * (HitTester.rebuild) when the scene snapshot rotates.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const DEFAULT_MAX_DEPTH = 6;
const DEFAULT_MAX_ITEMS = 8;
const DEFAULT_TOLERANCE = 4;
const rectsIntersect = (a, b) => !(a.x + a.w < b.x || b.x + b.w < a.x || a.y + a.h < b.y || b.y + b.h < a.y);
const pointInRect = (p, r, tol) => p.x >= r.x - tol && p.x <= r.x + r.w + tol && p.y >= r.y - tol && p.y <= r.y + r.h + tol;
export class Quadtree {
    root;
    idIndex = new Map();
    maxDepth;
    maxItems;
    constructor(bounds, opts = {}) {
        this.maxDepth = opts.maxDepth ?? DEFAULT_MAX_DEPTH;
        this.maxItems = opts.maxItems ?? DEFAULT_MAX_ITEMS;
        this.root = { bounds, depth: 0, items: [], children: null };
    }
    get size() {
        return this.idIndex.size;
    }
    insert(item) {
        if (this.idIndex.has(item.id)) {
            // Treat as replace — remove the stale entry, insert fresh.
            this.remove(item.id);
        }
        this.insertInto(this.root, item);
    }
    remove(id) {
        const node = this.idIndex.get(id);
        if (!node)
            return false;
        const idx = node.items.findIndex((it) => it.id === id);
        if (idx >= 0)
            node.items.splice(idx, 1);
        this.idIndex.delete(id);
        return true;
    }
    clear() {
        this.root.items = [];
        this.root.children = null;
        this.idIndex.clear();
    }
    queryPoint(p, tolerance = DEFAULT_TOLERANCE) {
        const out = [];
        this.collectPoint(this.root, p, tolerance, out);
        return out;
    }
    queryRect(rect) {
        const out = [];
        this.collectRect(this.root, rect, out);
        return out;
    }
    // ── internal recursion ────────────────────────────────────────────
    insertInto(node, item) {
        if (node.children) {
            const fits = this.fittingChild(node, item.bounds);
            if (fits !== null) {
                this.insertInto(fits, item);
                return;
            }
            // Straddles a quadrant boundary — keep at this node.
            node.items.push(item);
            this.idIndex.set(item.id, node);
            return;
        }
        node.items.push(item);
        this.idIndex.set(item.id, node);
        if (node.items.length > this.maxItems && node.depth < this.maxDepth) {
            this.subdivide(node);
        }
    }
    subdivide(node) {
        const { x, y, w, h } = node.bounds;
        const hw = w / 2;
        const hh = h / 2;
        const d = node.depth + 1;
        node.children = [
            { bounds: { x, y, w: hw, h: hh }, depth: d, items: [], children: null },
            { bounds: { x: x + hw, y, w: hw, h: hh }, depth: d, items: [], children: null },
            { bounds: { x, y: y + hh, w: hw, h: hh }, depth: d, items: [], children: null },
            { bounds: { x: x + hw, y: y + hh, w: hw, h: hh }, depth: d, items: [], children: null },
        ];
        const carry = node.items;
        node.items = [];
        for (const item of carry) {
            const fits = this.fittingChild(node, item.bounds);
            if (fits !== null) {
                fits.items.push(item);
                this.idIndex.set(item.id, fits);
            }
            else {
                node.items.push(item);
                this.idIndex.set(item.id, node);
            }
        }
    }
    fittingChild(node, bounds) {
        if (!node.children)
            return null;
        let result = null;
        for (const child of node.children) {
            if (bounds.x >= child.bounds.x &&
                bounds.y >= child.bounds.y &&
                bounds.x + bounds.w <= child.bounds.x + child.bounds.w &&
                bounds.y + bounds.h <= child.bounds.y + child.bounds.h) {
                if (result !== null)
                    return null; // ambiguous — sits on a seam
                result = child;
            }
        }
        return result;
    }
    collectPoint(node, p, tol, out) {
        if (!pointInRect(p, node.bounds, tol))
            return;
        for (const it of node.items) {
            if (pointInRect(p, it.bounds, tol))
                out.push(it);
        }
        if (node.children) {
            for (const child of node.children)
                this.collectPoint(child, p, tol, out);
        }
    }
    collectRect(node, rect, out) {
        if (!rectsIntersect(node.bounds, rect))
            return;
        for (const it of node.items) {
            if (rectsIntersect(it.bounds, rect))
                out.push(it);
        }
        if (node.children) {
            for (const child of node.children)
                this.collectRect(child, rect, out);
        }
    }
}
//# sourceMappingURL=quadtree.js.map